import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Copy, FolderSymlink, Check, Ban, RotateCcw } from "lucide-react";

const DUP_OPCIONES = [
  { key: "ficha", label: "Ficha técnica", desc: "Materiales, suela, estructura" },
  { key: "organizacion", label: "Organización del producto", desc: "Categoría, género, ciclo de vida…" },
  { key: "comercial", label: "Comercial / planeación", desc: "Costo meta, drop, compra" },
  { key: "multimedia", label: "Multimedia", desc: "Bocetos e imágenes" },
  { key: "notas", label: "Notas de desarrollo", desc: "Comentarios internos" },
  { key: "proceso", label: "Proceso (pasos y avance)", desc: "Copia el estado de los pasos" },
];

const REPLACE_EMPTY = { open: false, tipo: null, paso_numero: null, file: null, motivo: "" };

export default function ModeloDialogs({
  m, colecciones,
  replaceDlg, setReplaceDlg, confirmReplace,
  dupOpen, setDupOpen, dupNombre, setDupNombre, dupColeccion, setDupColeccion, dupSel, setDupSel, confirmarDuplicar, accionBusy,
  reasignOpen, setReasignOpen, reasignColeccion, setReasignColeccion, confirmarReasignar,
  reajusteOpen, setReajusteOpen, reajustePaso, setReajustePaso, reajusteMotivo, setReajusteMotivo, confirmarReajuste,
  depuradoOpen, setDepuradoOpen, depuradoMotivo, setDepuradoMotivo, confirmarDepurado,
  reactivarOpen, setReactivarOpen, reactivarMotivo, setReactivarMotivo, confirmarReactivar,
  lightbox, setLightbox,
}) {
  const pasosHechos = (m?.pasos || []).filter((p) => p.hecho).sort((a, b) => a.numero - b.numero);
  return (
    <>
      <Dialog open={reactivarOpen} onOpenChange={setReactivarOpen}>
        <DialogContent className="max-w-md" data-testid="reactivar-modal">
          <DialogTitle>Reactivar modelo</DialogTitle>
          <p className="text-sm text-slate-500 -mt-2">
            Retoma este modelo depurado. Vuelve a su categorización real y sus pasos vuelven a ser editables, <b>conservando su avance exacto</b>. El registro de la depuración original se conserva en el historial.
          </p>
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Textarea
              rows={3}
              value={reactivarMotivo}
              onChange={(e) => setReactivarMotivo(e.target.value)}
              placeholder="Motivo por el que se retoma (mínimo 10 caracteres)…"
              data-testid="reactivar-motivo"
            />
            <p className="text-[11px] text-slate-400">{(reactivarMotivo || "").trim().length}/10 caracteres mínimos</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivarOpen(false)} data-testid="reactivar-cancelar">Cancelar</Button>
            <Button onClick={confirmarReactivar} disabled={accionBusy || (reactivarMotivo || "").trim().length < 10} data-testid="reactivar-confirmar"><RotateCcw size={14} className="mr-1" /> Reactivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={depuradoOpen} onOpenChange={setDepuradoOpen}>
        <DialogContent className="max-w-md" data-testid="depurado-modal">
          <DialogTitle>Depurar modelo</DialogTitle>
          <p className="text-sm text-slate-500 -mt-2">
            Marca este modelo como <b>depurado</b> (descontinuado sin terminar). Permanece en su colección conservando su avance real, pero deja de contar en las métricas de cumplimiento y sus pasos quedan en solo lectura.
          </p>
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Textarea
              rows={3}
              value={depuradoMotivo}
              onChange={(e) => setDepuradoMotivo(e.target.value)}
              placeholder="Motivo por el que se descontinúa (mínimo 10 caracteres)…"
              data-testid="depurado-motivo"
            />
            <p className="text-[11px] text-slate-400">{(depuradoMotivo || "").trim().length}/10 caracteres mínimos</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepuradoOpen(false)} data-testid="depurado-cancelar">Cancelar</Button>
            <Button onClick={confirmarDepurado} disabled={accionBusy || (depuradoMotivo || "").trim().length < 10} data-testid="depurado-confirmar"><Ban size={14} className="mr-1" /> Depurar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reajusteOpen} onOpenChange={setReajusteOpen}>
        <DialogContent className="max-w-md" data-testid="reajuste-modal">
          <DialogTitle>Reajuste del proceso</DialogTitle>
          <p className="text-sm text-slate-500 -mt-2">
            Regresa el proceso a un paso anterior. Se desmarcarán ese paso y todos los posteriores que estén completados. Los documentos ya subidos NO se borran (quedan como referencia), pero cada paso volverá a pedir un documento nuevo para marcarse.
          </p>
          <div className="space-y-1.5">
            <Label>Paso al que se regresa</Label>
            <Select value={reajustePaso} onValueChange={setReajustePaso}>
              <SelectTrigger data-testid="reajuste-paso-select"><SelectValue placeholder="Elige un paso completado" /></SelectTrigger>
              <SelectContent>
                {pasosHechos.map((p) => <SelectItem key={p.numero} value={String(p.numero)}>{p.orden ?? p.numero}. {p.nombre} ({p.fase})</SelectItem>)}
              </SelectContent>
            </Select>
            {pasosHechos.length === 0 && <p className="text-[11px] text-slate-400">No hay pasos completados para regresar.</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Motivo del reajuste</Label>
            <Textarea
              rows={3}
              value={reajusteMotivo}
              onChange={(e) => setReajusteMotivo(e.target.value)}
              placeholder="Motivo del reajuste (mínimo 10 caracteres)…"
              data-testid="reajuste-motivo"
            />
            <p className="text-[11px] text-slate-400">{(reajusteMotivo || "").trim().length}/10 caracteres mínimos</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReajusteOpen(false)} data-testid="reajuste-cancelar">Cancelar</Button>
            <Button onClick={confirmarReajuste} disabled={accionBusy || !reajustePaso || (reajusteMotivo || "").trim().length < 10} data-testid="reajuste-confirmar">Confirmar reajuste</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={replaceDlg.open} onOpenChange={(o) => !o && setReplaceDlg(REPLACE_EMPTY)}>
        <DialogContent className="max-w-md" data-testid="reemplazo-doc-modal">
          <DialogTitle>Motivo del reemplazo</DialogTitle>
          <p className="text-sm text-slate-500 -mt-2">
            Vas a reemplazar el documento vigente{replaceDlg.file ? <> por <span className="font-medium text-slate-700">"{replaceDlg.file.name}"</span></> : null}. El archivo anterior no se elimina: queda como versión previa en el historial. Indica por qué lo cambias.
          </p>
          <Textarea
            rows={3}
            value={replaceDlg.motivo}
            onChange={(e) => setReplaceDlg((d) => ({ ...d, motivo: e.target.value }))}
            placeholder="Motivo del cambio (mínimo 10 caracteres)…"
            data-testid="reemplazo-doc-motivo"
          />
          <p className="text-[11px] text-slate-400">{(replaceDlg.motivo || "").trim().length}/10 caracteres mínimos</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceDlg(REPLACE_EMPTY)} data-testid="reemplazo-doc-cancelar">Cancelar</Button>
            <Button onClick={confirmReplace} disabled={(replaceDlg.motivo || "").trim().length < 10} data-testid="reemplazo-doc-confirmar">Confirmar reemplazo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-lg" data-testid="duplicar-modal">
          <DialogTitle>Duplicar modelo</DialogTitle>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nombre del nuevo modelo</Label>
              <Input value={dupNombre} onChange={(e) => setDupNombre(e.target.value)} data-testid="duplicar-nombre-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Colección</Label>
              <Select value={dupColeccion} onValueChange={setDupColeccion}>
                <SelectTrigger data-testid="duplicar-coleccion-select"><SelectValue placeholder="Elige una colección" /></SelectTrigger>
                <SelectContent>
                  {colecciones.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}{c.estado === "completada" ? " (completada)" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>¿Qué deseas duplicar?</Label>
              <div className="grid grid-cols-2 gap-2">
                {DUP_OPCIONES.map((opt) => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setDupSel((s) => ({ ...s, [opt.key]: !s[opt.key] }))}
                    data-testid={`duplicar-opt-${opt.key}`}
                    className={`flex items-start gap-2.5 text-left rounded-xl border p-3 transition-colors ${dupSel[opt.key] ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}
                  >
                    <span className={`mt-0.5 h-4 w-4 rounded-[4px] border flex items-center justify-center shrink-0 ${dupSel[opt.key] ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300"}`} aria-hidden="true">
                      {dupSel[opt.key] && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-slate-800">{opt.label}</span>
                      <span className="block text-[11px] text-slate-400 leading-tight">{opt.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400">El SKU se copia igual al original. Si no marcas "Proceso", los pasos se crean desde cero.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}>Cancelar</Button>
            <Button onClick={confirmarDuplicar} disabled={accionBusy || !dupColeccion} data-testid="duplicar-confirm"><Copy size={14} className="mr-1" /> Duplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reasignOpen} onOpenChange={setReasignOpen}>
        <DialogContent className="max-w-md" data-testid="reasignar-modal">
          <DialogTitle>Reasignar colección</DialogTitle>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Colección destino</Label>
              <Select value={reasignColeccion} onValueChange={setReasignColeccion}>
                <SelectTrigger data-testid="reasignar-coleccion-select"><SelectValue placeholder="Elige una colección" /></SelectTrigger>
                <SelectContent>
                  {colecciones.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}{c.id === m.coleccion_id ? " (actual)" : ""}{c.estado === "completada" ? " · completada" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-400">Solo cambia la colección del modelo. El avance, documentos e historial no se modifican.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasignOpen(false)}>Cancelar</Button>
            <Button onClick={confirmarReasignar} disabled={accionBusy || !reasignColeccion} data-testid="reasignar-confirm"><FolderSymlink size={14} className="mr-1" /> Reasignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2 bg-white" data-testid="lightbox-modal">
          <DialogTitle className="sr-only">{lightbox?.label || "Imagen"}</DialogTitle>
          {lightbox && <img src={lightbox.src} alt={lightbox.label} className="w-full max-h-[80vh] object-contain rounded-md" />}
        </DialogContent>
      </Dialog>
    </>
  );
}
